import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, Minus, ShoppingCart, Trash2, ScanLine, Loader2 } from "lucide-react";
import CameraScannerButton from "@/components/CameraScannerButton";
import { beep } from "@/lib/beep";
import { toast } from "sonner";

export default function PedidoNovo() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [produtos, setProdutos] = useState([]);
  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState("");
  const [busca, setBusca] = useState("");
  const [cart, setCart] = useState({}); // sku_id -> {produto, qtd}
  const [saving, setSaving] = useState(false);
  const [scan, setScan] = useState("");

  useEffect(() => {
    api.get("/produtos").then((r) => setProdutos(r.data.filter((p) => p.ativo && p.distribuivel !== false)));
    api.get("/units").then((r) => {
      const uu = r.data.filter((u) => u.tipo === "UNIDADE" && u.ativo !== false);
      setUnits(uu);
      if (uu.length) setUnitId(uu[0].id);
    });
  }, []);

  // Só busca ao digitar. Nunca carrega catálogo/categoria automaticamente.
  const results = useMemo(() => {
    const b = busca.trim().toLowerCase();
    if (!b) return [];
    return produtos.filter((p) =>
      p.nome.toLowerCase().includes(b) || (p.sku || "").toLowerCase().includes(b) || (p.ean || "").includes(b) || (p.marca || "").toLowerCase().includes(b)
    ).slice(0, 15);
  }, [produtos, busca]);

  const add = (p, q = 1) => setCart((c) => ({ ...c, [p.id]: { produto: p, qtd: (c[p.id]?.qtd || 0) + q } }));
  const setQ = (id, q) => setCart((c) => q <= 0 ? Object.fromEntries(Object.entries(c).filter(([k]) => k !== id)) : { ...c, [id]: { ...c[id], qtd: q } });
  const addFromSearch = (p) => { add(p, 1); try { beep(); } catch (_) {} setBusca(""); };

  const doScan = (codeArg) => {
    const code = (codeArg ?? scan).trim();
    if (!code) return;
    const p = produtos.find((x) => x.ean === code || x.sku === code || x.ean_master === code);
    if (!p) { toast.error("Produto não encontrado"); return; }
    const q = p.ean_master === code ? p.fator_conversao : 1;
    try { beep(); } catch (_) {}
    add(p, q);
    toast.success(`${p.nome} +${q}`);
    setScan("");
  };

  const items = Object.values(cart);
  const totalItens = items.reduce((s, i) => s + i.qtd, 0);

  const enviar = async () => {
    if (!unitId) return toast.error("Selecione a unidade");
    if (!items.length) return toast.error("Carrinho vazio");
    setSaving(true);
    try {
      const { data } = await api.post("/pedidos", {
        unit_id: unitId,
        itens: items.map((i) => ({ sku_id: i.produto.id, qtd_solicitada: i.qtd })),
      });
      toast.success(`Pedido ${data.numero} criado`);
      nav(`/pedidos/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar pedido");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="pedido-novo-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Solicitação da Unidade</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2"><ShoppingCart className="w-7 h-7 text-primary" /> Novo Pedido</h1>
        </div>
        <div className="min-w-[220px]">
          <Label className="text-xs">Unidade Solicitante</Label>
          <Select value={unitId} onValueChange={setUnitId}>
            <SelectTrigger data-testid="select-unidade"><SelectValue placeholder="Unidade" /></SelectTrigger>
            <SelectContent>{units.map((u) => <SelectItem key={u.id} value={u.id}>{u.codigo} — {u.nome}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-4 max-w-xl mx-auto lg:max-w-3xl pb-4">
        {/* Escanear / Bipar */}
        <div className="rounded-lg border border-primary/40 bg-black/40 p-4 space-y-3">
          <div className="flex items-center gap-2 text-primary text-xs font-bold uppercase tracking-[0.18em]"><ScanLine className="w-4 h-4" /> Escanear / Bipar</div>
          <div className="flex gap-2">
            <Input value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doScan()}
              placeholder="EAN / SKU / Master" inputMode="text" autoComplete="off" data-testid="cart-scan-input"
              className="text-xl font-mono h-12 bg-black/70 border-2 border-primary/60 text-primary" />
            <Button type="button" variant="outline" className="h-12" onClick={() => doScan()} data-testid="cart-scan-btn"><ScanLine className="w-5 h-5" /></Button>
            <CameraScannerButton continuous onDetected={(code) => doScan(code)} className="h-12 px-4 shrink-0" testid="cart-camera-btn" />
          </div>
          <p className="text-[11px] text-muted-foreground">No celular, use a câmera para bipar vários produtos em sequência sem reabrir.</p>
        </div>

        {/* Buscar manualmente (só mostra resultados ao digitar) */}
        <div className="space-y-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar produto, marca, SKU…" className="pl-9 h-12" data-testid="cart-busca" />
          </div>
          {busca.trim() && (
            <div className="space-y-1.5 max-h-[300px] overflow-y-auto rounded-md border border-border bg-card/40 p-1.5" data-testid="pedido-resultados">
              {results.map((p) => (
                <button key={p.id} onClick={() => addFromSearch(p)} data-testid={`add-${p.sku}`}
                  className="w-full text-left rounded-md hover:bg-secondary/50 px-3 py-2 flex items-center justify-between">
                  <div className="min-w-0"><div className="text-sm font-medium truncate">{p.nome}</div><div className="text-[10px] font-mono text-muted-foreground">{p.sku} · {p.marca} · {p.categoria}</div></div>
                  <Plus className="w-4 h-4 text-primary shrink-0" />
                </button>
              ))}
              {!results.length && <div className="text-sm text-muted-foreground p-3">Nenhum produto encontrado.</div>}
            </div>
          )}
        </div>

        {/* Carrinho */}
        <Card className="bg-card/70 p-4" data-testid="cart-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 font-display uppercase font-bold"><ShoppingCart className="w-5 h-5 text-primary" /> Carrinho</div>
            <span className="text-xs font-mono text-muted-foreground">{items.length} produto(s) · {totalItens} un</span>
          </div>
          <div className="space-y-2 min-h-[120px]">
            {!items.length && <div className="text-sm text-muted-foreground text-center py-10">Bipe ou busque produtos para adicionar ao pedido.</div>}
            {items.map(({ produto: p, qtd }) => (
              <div key={p.id} className="flex items-center gap-2 rounded-md border border-border bg-black/30 px-3 py-2" data-testid={`cart-item-${p.sku}`}>
                <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{p.nome}</div><div className="text-[10px] text-muted-foreground font-mono">{p.sku}</div></div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setQ(p.id, qtd - 1)} data-testid={`cart-minus-${p.sku}`}><Minus className="w-3 h-3" /></Button>
                  <Input value={qtd} onChange={(e) => setQ(p.id, Number(e.target.value) || 0)} className="w-16 h-9 text-center font-mono" data-testid={`cart-qtd-${p.sku}`} />
                  <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setQ(p.id, qtd + 1)} data-testid={`cart-plus-${p.sku}`}><Plus className="w-3 h-3" /></Button>
                  <Button variant="outline" size="icon" className="h-9 w-9 text-red-400" onClick={() => setQ(p.id, 0)} data-testid={`cart-del-${p.sku}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Solicitar Pedido — logo abaixo do carrinho, acompanha o conteúdo */}
        <Button className="w-full h-14 text-lg font-bold shadow-lg mb-[calc(env(safe-area-inset-bottom)+12px)]" disabled={saving || !items.length || !unitId} onClick={enviar} data-testid="btn-enviar-pedido">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Solicitar Pedido {items.length ? `· ${totalItens} un` : ""}</>}
        </Button>
      </div>
    </div>
  );
}
